import { useCallback, useEffect, useState } from "preact/hooks";

import {
  createPushSubscription,
  deletePushSubscription,
  fetchMyPushSubscriptions,
  fetchVapidPublicKey,
  patchMyReminders,
} from "../lib/api";
import {
  clearDeviceSubscriptionId,
  detectDevice,
  type DeviceInfo,
  getDeviceSubscriptionId,
  isPushSupported,
  pushCapability,
  readDeviceEnv,
  setDeviceSubscriptionId,
  urlBase64ToUint8Array,
} from "../lib/reminders";

/**
 * Ground truth for "will this device get a daily reminder right now":
 * - `checking` — the initial `PushManager.getSubscription()` probe hasn't
 *   resolved yet (first render only).
 * - `unsupported` — this browser has no `PushManager` at all, and it is
 *   not an iPhone/iPad (those get one of the two states below instead).
 * - `needs_install` — iPhone/iPad in a Safari/Chrome tab. Web push only
 *   exists in the Home Screen app there, so the row shows the steps to add
 *   it instead of a switch.
 * - `needs_update` — iPhone/iPad below iOS/iPadOS 16.4, which has no web
 *   push at all.
 * - `denied` — the OS/browser-level prompt was already declined; the app
 *   cannot re-ask (only the browser's own site settings can undo this).
 * - `subscribed` — this exact browser holds a live `PushSubscription`.
 * - `unsubscribed` — supported and not denied, but not currently
 *   subscribed (never asked, dismissed, or explicitly turned off).
 */
export type ReminderSupport =
  | "checking"
  | "unsupported"
  | "needs_install"
  | "needs_update"
  | "denied"
  | "subscribed"
  | "unsubscribed";

export interface UseReminderPrefsResult {
  support: ReminderSupport;
  /** Which phone/computer + browser this is — picks the right install/
   * unblock steps (`lib/reminderGuidance.ts`). */
  device: DeviceInfo;
  busy: boolean;
  error: string | null;
  /** Requests the browser permission if needed, subscribes this device
   * via `PushManager`, registers it with the backend, and turns the
   * server-side preference on. Returns `true` on success. Shared by both
   * the Today opt-in card ("Yes please") and the More tab's toggle
   * (turning it back on). */
  enable: () => Promise<boolean>;
  /** Unsubscribes this device (browser + backend) and turns the
   * server-side preference off. Shared by the More tab's toggle. */
  disable: () => Promise<void>;
}

/** Support short of asking the browser for a subscription: what this
 * device can do at all, or `null` when push is available here. */
function capabilitySupport(device: DeviceInfo): ReminderSupport | null {
  const capability = pushCapability(device, isPushSupported());
  return capability === "supported" ? null : capability;
}

async function readCurrentSupport(device: DeviceInfo): Promise<ReminderSupport> {
  const blocked = capabilitySupport(device);
  if (blocked) return blocked;
  if (Notification.permission === "denied") return "denied";
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return sub ? "subscribed" : "unsubscribed";
  } catch {
    // A genuinely broken service-worker registration reads the same as
    // "not currently subscribed" — `enable()` will surface any real
    // failure the next time it's attempted.
    return "unsubscribed";
  }
}

export function useReminderPrefs(): UseReminderPrefsResult {
  const [device] = useState<DeviceInfo>(() => detectDevice(readDeviceEnv()));
  const [support, setSupport] = useState<ReminderSupport>("checking");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void readCurrentSupport(device).then(setSupport);
  }, [device]);

  const enable = useCallback(async (): Promise<boolean> => {
    const blocked = capabilitySupport(device);
    if (blocked) {
      setSupport(blocked);
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      let permission = Notification.permission;
      if (permission === "default") permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setSupport(permission === "denied" ? "denied" : "unsubscribed");
        return false;
      }

      const { public_key: publicKey } = await fetchVapidPublicKey();
      if (!publicKey) {
        // Sprint-005 C5 backend note: an empty key means this deployment
        // has no VAPID pair configured — reminders are a no-op. Distinct,
        // honest failure rather than a silent no-op subscribe attempt.
        setError("Reminders aren't turned on for this farm yet.");
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // Cast: TS's DOM lib types `Uint8Array`'s own backing buffer as
          // the broader `ArrayBufferLike` (which includes
          // `SharedArrayBuffer`), while `PushSubscriptionOptionsInit`
          // narrowly wants `BufferSource` — `urlBase64ToUint8Array` always
          // constructs a plain (never shared) buffer, so this is a real,
          // safe narrowing, not a type escape hatch around a genuine bug.
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("subscribe() returned an incomplete PushSubscription");
      }

      const created = await createPushSubscription({
        idempotency_key: crypto.randomUUID(),
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setDeviceSubscriptionId(created.id);
      await patchMyReminders(true);
      setSupport("subscribed");
      return true;
    } catch {
      setError("Couldn't turn reminders on right now — try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [device]);

  const disable = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (isPushSupported()) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        const endpoint = subscription?.endpoint;
        if (subscription) await subscription.unsubscribe();

        let subscriptionId = getDeviceSubscriptionId();
        if (!subscriptionId && endpoint) {
          // Cold-path fallback (flagged): this device's id wasn't on file
          // locally (storage cleared, or subscribed before this key
          // existed) — look it up server-side by endpoint rather than
          // leaving a dangling, still-live row behind.
          const mine = await fetchMyPushSubscriptions();
          subscriptionId = mine.find((s) => s.endpoint === endpoint && !s.revoked_at)?.id ?? null;
        }
        if (subscriptionId) await deletePushSubscription(subscriptionId);
      }
      clearDeviceSubscriptionId();
      await patchMyReminders(false);
    } catch {
      setError("Couldn't turn reminders off right now — try again.");
    } finally {
      setSupport(capabilitySupport(device) ?? "unsubscribed");
      setBusy(false);
    }
  }, [device]);

  return { support, device, busy, error, enable, disable };
}
